import { useEffect, useState } from 'react';
import { parseProvisionStatus, type ProvisionStage } from './provisionStatus';

export type ReadinessState = 'idle' | 'checking' | 'ready' | 'timed-out';

export type GuardianReadiness = {
  state: ReadinessState;
  // The build's step-by-step progress, when the status endpoint reports it; empty until
  // the endpoint answers with stages (or when it only knows done-or-not).
  stages: ProvisionStage[];
};

type Options = {
  /** How often to ask. The huddle settled on every few seconds while the wheel shows. */
  intervalMs?: number;
  /** When to start saying "taking longer than expected". The instance itself builds in about
   *  a minute, but a brand-new org's address (org-edge + DNS) can take 5-10 minutes, so the
   *  default is generous — and polling continues past it either way. */
  timeoutMs?: number;
  /** Supplies the WorkOS access token once the status endpoint verifies callers; omit while it is open. */
  getToken?: () => Promise<string | null>;
};

type Terminal = { url: string; state: 'ready' | 'timed-out' };
type Stages = { url: string; list: ProvisionStage[] };

// Polls a provisioning status endpoint until it answers done. Transport-agnostic on purpose:
// whether the endpoint ends up hosted on the Guardian instance itself or on a status
// middleware, the hub side is the same — a URL that eventually says the build is done
// (parseProvisionStatus is the one seam that knows the response shape). While the instance
// is still being built nothing answers, so fetch failures mean "keep waiting", never "failed".
export function useGuardianReadiness(
  statusUrl: string | null,
  { intervalMs = 3_000, timeoutMs = 900_000, getToken }: Options = {},
): GuardianReadiness {
  // Only terminal outcomes are stored; idle/checking are derived from the URL, so a
  // changed URL starts a fresh polling session without any render-time state writes.
  const [terminal, setTerminal] = useState<Terminal | null>(null);
  const [stages, setStages] = useState<Stages | null>(null);

  useEffect(() => {
    if (!statusUrl) return;

    let cancelled = false;
    const maxTicks = Math.ceil(timeoutMs / intervalMs);
    let ticks = 0;

    const probe = async () => {
      let parsed = null;
      try {
        const token = getToken ? await getToken() : null;
        const res = await fetch(statusUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (res.ok) parsed = parseProvisionStatus(await res.json());
      } catch {
        parsed = null; // nothing answering yet — still being built
      }
      if (cancelled || !parsed) return;
      const list = parsed.stages;
      if (list.length > 0) {
        // Same-content updates return the previous object so a steady answer doesn't
        // re-render the page every tick.
        setStages((s) =>
          s?.url === statusUrl && JSON.stringify(s.list) === JSON.stringify(list)
            ? s
            : { url: statusUrl, list },
        );
      }
      if (parsed.done) {
        clearInterval(timer);
        setTerminal({ url: statusUrl, state: 'ready' });
      }
    };

    const timer = setInterval(() => {
      ticks += 1;
      // Timing out changes what the UI says, not what the poll does: polling continues so
      // a build that finishes late still flips to ready — the timed-out copy promises it.
      if (ticks === maxTicks && !cancelled) {
        setTerminal({ url: statusUrl, state: 'timed-out' });
      }
      void probe();
    }, intervalMs);
    void probe();

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
    // getToken is deliberately not a dependency: callers pass inline closures, and the
    // poll must not restart on every render. The URL defines the polling session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusUrl, intervalMs, timeoutMs]);

  if (!statusUrl) return { state: 'idle', stages: [] };
  return {
    state: terminal?.url === statusUrl ? terminal.state : 'checking',
    stages: stages?.url === statusUrl ? stages.list : [],
  };
}
